import { Head } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { HardDrive, AlertCircle, CheckCircle, Shield, Zap } from "lucide-react"

export default function LimitFileSize() {
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <Head title="Limit File Size - Documentation" />
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">File Size Limitation</h1>
        <p className="text-lg text-gray-600 mb-4">
          The SimpleFileUpload web component includes built-in file size validation that allows you to restrict the maximum size of uploaded files using the <code className="bg-gray-100 px-2 py-1 rounded">max-file-size</code> attribute.
        </p>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <Shield className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>TLDR:</strong> Add a "max-file-size" attribute with a value in bytes (e.g. <code>max-file-size="10485760"</code> for 10MB)
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
          <CardDescription>
            Understanding the file size validation process
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            The component validates file sizes before upload using the <code className="bg-gray-100 px-2 py-1 rounded">max-file-size</code> attribute. When a file is selected or dropped, the component checks if its size exceeds the limit before proceeding with the upload.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-800 mb-1">What It Does</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Rejects files larger than specified size</li>
                    <li>• Shows user-friendly error messages</li>
                    <li>• Prevents large files from uploading</li>
                    <li>• Allows other valid files in multi-file mode</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start">
                <Zap className="w-5 h-5 text-purple-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-800 mb-1">Benefits</h4>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• Client-side validation saves bandwidth</li>
                    <li>• Immediate user feedback</li>
                    <li>• Default is 5MB if not specified</li>
                    <li>• Works with drag-and-drop</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Plan-Based Maximum Limits</CardTitle>
          <CardDescription>
            Maximum file sizes are enforced based on your subscription plan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">Basic Plan</h4>
                <Badge variant="secondary">5 MB</Badge>
              </div>
              <p className="text-sm text-gray-600">Maximum file size per upload</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">Pro Plan</h4>
                <Badge variant="secondary">50 MB</Badge>
              </div>
              <p className="text-sm text-gray-600">Maximum file size per upload</p>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-500 mr-2 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-700">
                  <strong>Note:</strong> These are hard limits enforced by the server. You cannot set max-file-size higher than your plan allows.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Basic Usage</CardTitle>
          <CardDescription>
            Add the max-file-size attribute to limit file sizes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-2">Simple Example</h3>
            <p className="text-gray-600 mb-3">
              Limit uploads to 10MB (10,485,760 bytes):
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<simple-file-upload 
  public-key="your-key"
  max-file-size="10485760"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-2">Common Size Limits</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg text-center">
                <div className="font-semibold">1 MB</div>
                <code className="text-xs text-gray-600">1048576</code>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-center">
                <div className="font-semibold">5 MB</div>
                <code className="text-xs text-gray-600">5242880</code>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-center">
                <div className="font-semibold">10 MB</div>
                <code className="text-xs text-gray-600">10485760</code>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg text-center">
                <div className="font-semibold">50 MB</div>
                <code className="text-xs text-gray-600">52428800</code>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Usage Examples</CardTitle>
          <CardDescription>
            Different scenarios for file size limits
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Image Uploads</h3>
            <p className="text-gray-600 mb-3">
              Limit image uploads to 5MB:
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<simple-file-upload 
  public-key="your-key"
  max-file-size="5242880"
  accept="image/*"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Document Uploads</h3>
            <p className="text-gray-600 mb-3">
              Allow larger documents up to 20MB:
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<simple-file-upload 
  public-key="your-key"
  max-file-size="20971520"
  accept="application/pdf,application/msword"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Multiple Files with Size Limit</h3>
            <p className="text-gray-600 mb-3">
              Each file must be under 2MB:
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<simple-file-upload 
  public-key="your-key"
  max-file-size="2097152"
  max-files="5"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Error Handling</CardTitle>
          <CardDescription>
            How the component handles oversized files
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            When a file exceeds the maximum size, the component:
          </p>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-800 mb-1">Error Message Example</h4>
                <p className="text-sm text-red-700">
                  "File exceeds maximum size of 10 MB"
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium">Error Behavior:</h4>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>The oversized file is rejected and not uploaded</li>
              <li>A clear error message is displayed to the user</li>
              <li>The file size is automatically converted to a readable format (KB, MB, etc.)</li>
              <li>Other valid files can still be uploaded in multi-file mode</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Best Practices</CardTitle>
          <CardDescription>
            Tips for implementing file size limits effectively
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start">
              <HardDrive className="w-5 h-5 mr-2 mt-0.5 text-blue-500" />
              <div>
                <strong>Set reasonable limits:</strong> Consider your use case and user needs when setting size limits
              </div>
            </li>
            <li className="flex items-start">
              <HardDrive className="w-5 h-5 mr-2 mt-0.5 text-blue-500" />
              <div>
                <strong>Inform users upfront:</strong> Display the file size limit clearly near the upload area
              </div>
            </li>
            <li className="flex items-start">
              <HardDrive className="w-5 h-5 mr-2 mt-0.5 text-blue-500" />
              <div>
                <strong>Consider file types:</strong> Different file types may need different size limits
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Size Calculation Reference</CardTitle>
          <CardDescription>
            Quick reference for converting file sizes to bytes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-mono mb-3">1 KB = 1,024 bytes</p>
            <p className="text-sm font-mono mb-3">1 MB = 1,048,576 bytes</p>
            <p className="text-sm font-mono mb-3">1 GB = 1,073,741,824 bytes</p>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                <strong>Formula:</strong> bytes = size_in_mb × 1024 × 1024
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
